import {chunk} from 'lodash';

import {makeApiRequest} from './api';
import {DashboardSection} from './dashboardSection';

export {DashboardSection} from './dashboardSection';

export enum Source {
  HRRR = 'hrrr',
  ECCC = 'eccc',
  ECMWFHRES = 'ecmwf-hres',
  WEATHERNEXT_2 = 'weathernext-2',
  GFS = 'gfs',
  GEFS = 'gefs',
  GEFS_HISTORICAL_MONTHLY = 'historical-percentile-monthly-gefs',
  GEFS_HISTORICAL_DAILY = 'historical-percentile-daily-gefs',
  SNODAS = 'snodas',
  SWANN = 'swann',
  MODIS_NDSI = 'modis-ndsi',
  MODIS_NDVI = 'modis-ndvi',
  HFObservations = 'hf-observations',
  HydroForecastShortTerm = 'hydroforecast-short-term',
  HydroForecastShortTerm2 = 'hydroforecast-short-term-2',
  HydroForecastShortTerm3 = 'hydroforecast-short-term-3',
  HydroForecastSeasonal = 'hydroforecast-seasonal',
  HydroForecastSeasonalAllTraces = 'hydroforecast-seasonal-all-traces',
  HydroForecastSeasonal3 = 'hydroforecast-seasonal-3',
  HydroForecastSeasonal3InternalPreview = 'hydroforecast-seasonal-3-internal-preview',
  HydroForecastSeasonal3ERA5 = 'hydroforecast-seasonal-3-era5',
  HydroForecastSeasonal3GEFS = 'hydroforecast-seasonal-3-gefs',
  RFC = 'rfc',
  NWRFC = 'nwrfc-short-term-forecast-v2',
  NWRFC_ESP_NATURAL = 'nwrfc-esp-natural',
  CBRFC = 'cbrfc',
  CNRFC_ESP_NATURAL = 'cnrfc-esp-natural',
  CNRFC = 'cnrfc',
  COMPOUND_RFC = 'compound-rfc',
  USGS = 'usgs',
  NET_DISCHARGE = 'net-discharge',
  PRIVATE_GAUGE_DATA = 'private-gauge-data',
  CO_DWR = 'co-dwr',
  CA_DWR = 'ca-dwr',
  USACE = 'usace',
  CEHQ_GAUGE = 'CEHQ-gauge',
  PACIFICORP_GAUGE = 'pacificorp-gauge',
  AUCKLAND_GAUGE = 'auckland-gauge',
  CPFL_GAUGE = 'cpfl-gauge',
  PGE_GAUGE = 'pge-gauge',
  EXTERNAL_GAUGE_DATA = 'external-gauge-data',
  COMPOUND_GAUGE_DATA = 'compound-gauge-data',
  NWRFC_NATURAL_RUNOFF = 'nwrfc-natural-runoff',
  FLOW_HISTORICAL_DAILY = 'historical-percentile-daily-gauge-observation',
  FLOW_HISTORICAL_MONTHLY = 'historical-percentile-monthly-gauge-observation',
  MODIS_NDSI_HISTORICAL_DAILY = 'historical-percentile-daily-modis-ndsi',
  MODIS_NDVI_HISTORICAL_DAILY = 'historical-percentile-daily-modis-ndvi',
  SNODAS_HISTORICAL_DAILY = 'historical-percentile-daily-snodas',
}

// The chart matches series to config items by source, so each model drawn on an H5 chart needs
// its own. The default model keeps the section's source and the others derive theirs from it and
// the model's display name, which is what the table and CSV columns show. A section's names are
// unique (modelDisplayNames), so the sources are too.
export function modelSource(sectionSource: Source, modelName: string): Source {
  return `${sectionSource}:${modelName}` as Source;
}

// The section source a model's source derives from. No Source value holds a ':'.
export function sectionSourceOf(source: Source): Source {
  return source.split(':')[0] as Source;
}

export const FLOW_OBSERVATION_SOURCES = [
  Source.USGS,
  Source.PRIVATE_GAUGE_DATA,
  Source.NET_DISCHARGE,
  Source.CO_DWR,
  Source.CA_DWR,
  Source.USACE,
  Source.CEHQ_GAUGE,
  Source.PACIFICORP_GAUGE,
  Source.ECCC,
  Source.AUCKLAND_GAUGE,
  Source.PGE_GAUGE,
  Source.CPFL_GAUGE,
  Source.EXTERNAL_GAUGE_DATA,
  Source.COMPOUND_GAUGE_DATA,
  Source.NWRFC_NATURAL_RUNOFF,
  Source.HFObservations,
];

export function isRFCSource(source: Source): boolean {
  return (
    source === Source.NWRFC ||
    source === Source.RFC ||
    source === Source.COMPOUND_RFC ||
    source === Source.CBRFC ||
    source === Source.CNRFC
  );
}

export enum WeatherColumns {
  PRECIPITATION = 'precipitation',
  TEMPERATURE = 'temperature',
}
export enum WeatherHistoricalDailyColumns {
  PRECIPITATION_0_05 = 'precipitationDailyQuantile0.05',
  PRECIPITATION_0_25 = 'precipitationDailyQuantile0.25',
  PRECIPITATION_MEAN = 'precipitationDailyMean',
  PRECIPITATION_MEDIAN = 'precipitationDailyMedian',
  PRECIPITATION_0_75 = 'precipitationDailyQuantile0.75',
  PRECIPITATION_0_95 = 'precipitationDailyQuantile0.95',
  TEMPERATURE_0_05 = 'temperatureDailyQuantile0.05',
  TEMPERATURE_0_25 = 'temperatureDailyQuantile0.25',
  TEMPERATURE_MEAN = 'temperatureDailyMean',
  TEMPERATURE_MEDIAN = 'temperatureDailyMedian',
  TEMPERATURE_0_75 = 'temperatureDailyQuantile0.75',
  TEMPERATURE_0_95 = 'temperatureDailyQuantile0.95',
}
export enum WeatherHistoricalMonthlyColumns {
  PRECIPITATION_0_05 = 'precipitationMonthlyQuantile0.05',
  PRECIPITATION_0_25 = 'precipitationMonthlyQuantile0.25',
  PRECIPITATION_MEAN = 'precipitationMonthlyMean',
  PRECIPITATION_MEDIAN = 'precipitationMonthlyMedian',
  PRECIPITATION_0_75 = 'precipitationMonthlyQuantile0.75',
  PRECIPITATION_0_95 = 'precipitationMonthlyQuantile0.95',
  TEMPERATURE_0_05 = 'temperatureMonthlyQuantile0.05',
  TEMPERATURE_0_25 = 'temperatureMonthlyQuantile0.25',
  TEMPERATURE_MEAN = 'temperatureMonthlyMean',
  TEMPERATURE_MEDIAN = 'temperatureMonthlyMedian',
  TEMPERATURE_0_75 = 'temperatureMonthlyQuantile0.75',
  TEMPERATURE_0_95 = 'temperatureMonthlyQuantile0.95',
}
export enum ElevationHistoricalDailyColumns {
  ELEVATION_0_05 = 'reservoirElevationDailyQuantile0.05',
  ELEVATION_0_25 = 'reservoirElevationDailyQuantile0.25',
  ELEVATION_MEAN = 'reservoirElevationDailyMean',
  ELEVATION_MEDIAN = 'reservoirElevationDailyMedian',
  ELEVATION_0_75 = 'reservoirElevationDailyQuantile0.75',
  ELEVATION_0_95 = 'reservoirElevationDailyQuantile0.95',
}
export enum FlowHistoricalDailyColumns {
  FLOW_0_05 = 'flowDailyQuantile0.05',
  FLOW_0_25 = 'flowDailyQuantile0.25',
  FLOW_MEAN = 'flowDailyMean',
  FLOW_MEDIAN = 'flowDailyMedian',
  FLOW_0_75 = 'flowDailyQuantile0.75',
  FLOW_0_95 = 'flowDailyQuantile0.95',
}
export enum FlowHistoricalMonthlyColumns {
  FLOW_0_05 = 'flowMonthlyQuantile0.05',
  FLOW_0_25 = 'flowMonthlyQuantile0.25',
  FLOW_MEAN = 'flowMonthlyMean',
  FLOW_MEDIAN = 'flowMonthlyMedian',
  FLOW_0_75 = 'flowMonthlyQuantile0.75',
  FLOW_0_95 = 'flowMonthlyQuantile0.95',
}
export enum FlowForecastColumns {
  DISCHARGE_0_01 = 'discharge_q0.01',
  DISCHARGE_0_025 = 'discharge_q0.025',
  DISCHARGE_0_05 = 'discharge_q0.05',
  DISCHARGE_0_10 = 'discharge_q0.1',
  DISCHARGE_0_25 = 'discharge_q0.25',
  DISCHARGE_MEAN = 'discharge_mean',
  DISCHARGE_MEDIAN = 'discharge_q0.5',
  DISCHARGE_0_75 = 'discharge_q0.75',
  DISCHARGE_0_90 = 'discharge_q0.9',
  DISCHARGE_0_95 = 'discharge_q0.95',
  DISCHARGE_0_975 = 'discharge_q0.975',
  DISCHARGE_0_99 = 'discharge_q0.99',
}
export enum RFCColumns {
  FLOW = 'flow',
}
export enum NWRFCColumns {
  DISCHARGE = 'discharge',
  INFLOW = 'inflow',
}

export enum CBRFCColumns {
  FLOW = 'flow',
}

export enum CNRFCColumns {
  DISCHARGE_ENSEMBLE = 'ensemble_forecast_avg',
  DISCHARGE_DETERMINISTIC = 'deterministic_forecast',
}

export enum CompoundRFCColumns {
  DISCHARGE = 'compound_rfc_forecast',
}

export enum FlowObservationColumns {
  DISCHARGE = 'discharge',
  DISCHARGE_RAW = 'discharge_raw',
  observed_clean = 'observed_clean',
  FLOW = 'flow',
  INFLOW = 'inflow',
  OUTFLOW = 'outflow',
  // USACE columns
  HAH_FLOW = 'HAH.Flow-In.Inst.1Hour.0.NWSRADIO-SMOOTHED-RAW',
  HGH_FLOW = 'HGH.Flow-In.Ave.~1Day.1Day.CBT-REV',
  DWR_FLOW = 'DWR.Flow-In.Ave.1Hour.1Hour.CBT-COMPUTED-REV',
  LIB_FLOW = 'LIB.Flow-In.Ave.~1Day.1Day.CBT-REV',
  // Hydromet columns
  NATURAL_FLOW = 'natural_flow',
}
export enum NDVIObservationColumns {
  NDVI_MAX = 'ndviRolling16DayMax',
}
export enum NDVIHistoricalColumns {
  NDVI_0_05 = 'ndviRolling16DayMaxDailyQuantile0.05',
  NDVI_0_25 = 'ndviRolling16DayMaxDailyQuantile0.25',
  NDVI_MEAN = 'ndviRolling16DayMaxDailyMean',
  NDVI_MEDIAN = 'ndviRolling16DayMaxDailyMedian',
  NDVI_0_75 = 'ndviRolling16DayMaxDailyQuantile0.75',
  NDVI_0_95 = 'ndviRolling16DayMaxDailyQuantile0.95',
}
export enum NDSIObservationColumns {
  NDSI_MEDIAN = 'ndsiRolling16DayMedian',
}
export enum NDSIHistoricalColumns {
  NDSI_0_05 = 'ndsiRolling16DayMedianDailyQuantile0.05',
  NDSI_0_25 = 'ndsiRolling16DayMedianDailyQuantile0.25',
  NDSI_MEAN = 'ndsiRolling16DayMedianDailyMean',
  NDSI_MEDIAN = 'ndsiRolling16DayMedianDailyMedian',
  NDSI_0_75 = 'ndsiRolling16DayMedianDailyQuantile0.75',
  NDSI_0_95 = 'ndsiRolling16DayMedianDailyQuantile0.95',
}
export enum SWEObservationColumns {
  SWE = 'snowWaterEquivalent',
}
export enum SWEHistoricalColumns {
  SWE_0_05 = 'snowWaterEquivalentDailyQuantile0.05',
  SWE_0_25 = 'snowWaterEquivalentDailyQuantile0.25',
  SWE_MEAN = 'snowWaterEquivalentDailyMean',
  SWE_MEDIAN = 'snowWaterEquivalentDailyMedian',
  SWE_0_75 = 'snowWaterEquivalentDailyQuantile0.75',
  SWE_0_95 = 'snowWaterEquivalentDailyQuantile0.95',
}
export type AllColumns =
  | ElevationHistoricalDailyColumns
  | FlowHistoricalDailyColumns
  | FlowHistoricalMonthlyColumns
  | FlowObservationColumns
  | FlowForecastColumns
  | RFCColumns
  | NWRFCColumns
  | CBRFCColumns
  | CNRFCColumns
  | CompoundRFCColumns
  | WeatherColumns
  | WeatherHistoricalDailyColumns
  | WeatherHistoricalMonthlyColumns
  | NDVIHistoricalColumns
  | NDVIObservationColumns
  | NDSIHistoricalColumns
  | NDSIObservationColumns
  | SWEHistoricalColumns
  | SWEObservationColumns;

export const ColumnsBySource: {[key in Source]?: AllColumns} = {
  [Source.FLOW_HISTORICAL_DAILY as string]: FlowHistoricalDailyColumns,
  [Source.FLOW_HISTORICAL_MONTHLY as string]: FlowHistoricalMonthlyColumns,
  [Source.USGS as string]: FlowObservationColumns,
  [Source.PRIVATE_GAUGE_DATA as string]: FlowObservationColumns,
  [Source.NET_DISCHARGE as string]: FlowObservationColumns,
  [Source.CO_DWR as string]: FlowObservationColumns,
  [Source.CA_DWR as string]: FlowObservationColumns,
  [Source.USACE as string]: FlowObservationColumns,
  [Source.CEHQ_GAUGE as string]: FlowObservationColumns,
  [Source.ECCC as string]: FlowObservationColumns,
  [Source.PACIFICORP_GAUGE as string]: FlowObservationColumns,
  [Source.AUCKLAND_GAUGE as string]: FlowObservationColumns,
  [Source.CPFL_GAUGE as string]: FlowObservationColumns,
  [Source.PGE_GAUGE as string]: FlowObservationColumns,
  [Source.EXTERNAL_GAUGE_DATA as string]: FlowObservationColumns,
  [Source.COMPOUND_GAUGE_DATA as string]: FlowObservationColumns,
  [Source.HydroForecastShortTerm as string]: FlowForecastColumns,
  [Source.RFC as string]: RFCColumns,
  [Source.CBRFC as string]: CBRFCColumns,
  [Source.NWRFC as string]: NWRFCColumns,
  [Source.NWRFC_ESP_NATURAL as string]: FlowForecastColumns,
  [Source.CNRFC_ESP_NATURAL as string]: FlowForecastColumns,
  [Source.HydroForecastSeasonal as string]: FlowForecastColumns,
  [Source.HRRR as string]: WeatherColumns,
  [Source.GFS as string]: WeatherColumns,
  [Source.GEFS as string]: WeatherColumns,
  [Source.GEFS_HISTORICAL_MONTHLY as string]: WeatherHistoricalMonthlyColumns,
  [Source.GEFS_HISTORICAL_DAILY as string]: WeatherHistoricalDailyColumns,
  [Source.ECMWFHRES as string]: WeatherColumns,
  [Source.WEATHERNEXT_2 as string]: WeatherColumns,
  [Source.MODIS_NDVI as string]: NDVIObservationColumns,
  [Source.MODIS_NDVI_HISTORICAL_DAILY as string]: NDVIHistoricalColumns,
  [Source.MODIS_NDSI as string]: NDSIObservationColumns,
  [Source.MODIS_NDSI_HISTORICAL_DAILY as string]: NDSIHistoricalColumns,
  [Source.SNODAS as string]: SWEObservationColumns,
  [Source.SWANN as string]: SWEObservationColumns,
  [Source.SNODAS_HISTORICAL_DAILY as string]: SWEHistoricalColumns,
};

export const ColumnDisplay: {[key in AllColumns]?: string} = {
  [WeatherColumns.PRECIPITATION]: 'precipitation',
  [WeatherColumns.TEMPERATURE]: 'temperature',
  [FlowForecastColumns.DISCHARGE_0_05]: '90% CI min',
  [FlowForecastColumns.DISCHARGE_0_25]: '50% CI min',
  [FlowForecastColumns.DISCHARGE_MEDIAN]: 'median',
  [FlowForecastColumns.DISCHARGE_MEAN]: 'mean',
  [FlowForecastColumns.DISCHARGE_0_75]: '50% CI max',
  [FlowForecastColumns.DISCHARGE_0_95]: '90% CI max',
  [RFCColumns.FLOW]: 'flow',
  [NWRFCColumns.DISCHARGE]: 'discharge',
  [NWRFCColumns.INFLOW]: 'inflow',
  [FlowHistoricalDailyColumns.FLOW_MEAN]: 'mean',
  [SWEObservationColumns.SWE]: 'snow water equivalent',
  [SWEHistoricalColumns.SWE_MEAN]: 'mean',
  [SWEHistoricalColumns.SWE_0_05]: '90% CI min',
  [SWEHistoricalColumns.SWE_0_95]: '90% CI max',
  [NDSIObservationColumns.NDSI_MEDIAN]: '',
  [NDSIHistoricalColumns.NDSI_MEAN]: 'mean',
  [NDSIHistoricalColumns.NDSI_0_05]: '90% CI min',
  [NDSIHistoricalColumns.NDSI_0_95]: '90% CI max',
  [NDVIObservationColumns.NDVI_MAX]: '',
  [NDVIHistoricalColumns.NDVI_MEAN]: 'mean',
  [NDVIHistoricalColumns.NDVI_0_05]: '90% CI min',
  [NDVIHistoricalColumns.NDVI_0_95]: '90% CI max',
};

export type ForecastTime = {
  initializationTime: Date;
  issueTime: Date;
  inputInitializationTimes?: {[key in Source]?: Date};
  // When the forecast was first published. Missing for forecasts published before the API
  // started recording it (mid-2025) and for sources that aren't production forecasts.
  firstIssueTime?: Date;
  // When the forecast was last republished. Unlike `issueTime` this is never swapped out for
  // the initialization time, so it's the only field that dates a backfill or a late update.
  lastUpdatedTime?: Date;
  query?: ForecastTimeSeriesQuery;
};
export type ForecastTimesBySource = {[key in Source]?: ForecastTime[]};
export type TimeAggregation = '1H' | '6H' | '1D' | '10D' | '30D' | '90D' | 'MS' | 'QS';
export type AggregationMethod = 'min' | 'max' | 'mean';
export type DataAggregation = 'volume' | 'rate';
export const RATE_VOLUME_MODES = [
  'rate',
  'volume',
  'cumulative-volume',
  'cumulative-volume-from-now',
  'level',
] as const;
export type RateVolumeMode = (typeof RATE_VOLUME_MODES)[number];
export const isCumulativeMode = (mode: RateVolumeMode): boolean =>
  mode === 'cumulative-volume' || mode === 'cumulative-volume-from-now';

export type ApiParams = {
  queries: ObservationTimeSeriesQuery[] | ForecastTimeSeriesQuery[];
};

export type AggregationOptions = {
  timeAggregation?: TimeAggregation;
  aggregationMethod?: AggregationMethod;
  rateVolumeMode?: RateVolumeMode;
  includeFlowContributingAreas?: boolean;
};

export type ObservationTimeSeriesQuery = {
  siteId: string;
  projectId?: string;
  source: Source;
  startDate?: Date;
  endDate?: Date;
  timeZone?: string;
  columns?: string[];
  unitSystem?: UnitSystem;
  // The dashboard section whose plot issued this query. Omitted for non-section
  // fetches (gauge/surface plots, eval tooling) and by the forecastTimes path.
  section?: DashboardSection;
} & AggregationOptions;

export type TimeSeriesData = {
  [key: string]: (number | null)[];
};

export type ApiObservationTimeSeries = {
  query: ObservationTimeSeriesQuery;
  data: TimeSeriesData;
  dataTimeRange: [Date, Date];
  timestamps: Date[];
  variables: TimeSeriesVariables;
  warnings?: string[];
};

export type ForecastTimeSeriesQuery = {
  siteId: string;
  projectId?: string;
  source: Source;
  initializationTimes?: Date[];
  initializationTimeStartDate?: Date;
  initializationTimeEndDate?: Date;
  timeZone?: string;
  columns?: AllColumns[];
  unitSystem?: UnitSystem;
  sourceMetadata?: {
    modelGeneration: string;
    traces: Array<Source | 'blend'>; //TODO(eva): maybe these won't be Sources
    customerVisiblePreviewMode?: boolean;
  };
  forecastLengthDays?: number;
  // The dashboard section whose plot issued this query. Omitted for non-section
  // fetches (gauge/surface plots, eval tooling) and by the forecastTimes path.
  section?: DashboardSection;
} & AggregationOptions;

export type ApiForecast = {
  initializationTime: Date;
  issueTime: Date;
  data: TimeSeriesData;
  validTimeRange: [Date, Date];
  validTimes: Date[];
};

export type SeasonalVolumeForecastQuery = {
  siteId: string;
  initializationTimes: Date[];
  volumePeriodStartDate: Date;
  volumePeriodEndDate: Date;
  normalize?: 'LTA';
  timeZone?: string;
  unitSystem?: UnitSystem;
  /**
   * Optional thresholds (fractions of the long-term average) that split the forecast
   * distribution into categories. The web UI recomputes categories client-side and leaves
   * this unset; external API consumers may pass it to get server-computed categories.
   */
  thresholds?: number[];
};

export type ApiSeasonalVolumeResponse = {
  forecasts: (ApiSeasonalVolumeForecast & {error: string})[];
  historicalPercentiles: {
    data: {[key in FlowHistoricalDailyColumns]: number};
    dateTimeRange: [Date, Date];
    timestamps: Date[];
    variables: TimeSeriesVariables;
  };
  observedVolumeToDate: {
    data: {[key in FlowObservationColumns | string]: number};
    dateTimeRange: [Date, Date];
    timestamps: Date[];
    variables: TimeSeriesVariables;
  };
  query: SeasonalVolumeForecastQuery;
  /** Effective thresholds (fractions of the LTA) the server used to compute `percentChance`. */
  thresholds: number[];
  variables: TimeSeriesVariables;
};

export type ApiSeasonalVolumeForecast = {
  forecastTimeRange: [Date, Date];
  initializationTime: Date;
  issueTime: Date;
  observedTimeRange: [Date, Date];
  observedVolume: number;
  totalVolume: {[key in FlowForecastColumns]: number};
  /** Long-term average in the same units as `totalVolume` (100 when normalizing by LTA). */
  observedLta: number;
  /**
   * Server-computed category probabilities keyed by dynamic labels like "< 90% LTA". The web
   * UI recomputes these client-side from `totalVolume`/`observedLta` so thresholds can be
   * adjusted without a round-trip; see `computePercentChance` in SeasonalVolume.tsx.
   */
  percentChance: {[label: string]: number};
};

type RequestContext = {
  /**
   * Organization context id for this request. If provided, this will be sent as `cOrgId` and
   * we’ll avoid attaching the global `x-context-organization-id` header to prevent mismatches.
   */
  contextOrgId?: string | null;
};

export type TimeSeriesVariables = {
  [key in AllColumns]?: {unit: string};
};

export type ApiForecastTimeSeries = {
  forecasts: ApiForecast[];
  query: ForecastTimeSeriesQuery;
  variables: TimeSeriesVariables;
  warnings?: string[];
};

export enum UnitSystem {
  US = 'US',
  Metric = 'Metric',
}

export type ApiForecastTimes = {
  issueTimes: string[]; // iso string, to be cast to date when parsed
  initializationTimes: string[]; // iso string, to be cast to date when parsed
  inputInitializationTimes?: {[key in Source]: string}[]; // iso string, to be cast when parsed
  firstIssueTimes?: (string | null)[]; // iso string, to be cast to date when parsed
  lastUpdatedTimes?: (string | null)[]; // iso string, to be cast to date when parsed
  query: ForecastTimeSeriesQuery;
};

export enum SeasonalModelVisualMode {
  // used to determine which combination of seasonal visuals to show in the UI
  Seasonal2Release = 'Seasonal-2-Release',
  Seasonal3Release = 'Seasonal-3-Release',
  Seasonal2ReleaseWithSeasonal3CustomerPreview = 'Seasonal-2-Release-with-Seasonal-3-Customer-Preview',
  Seasonal2ReleaseWithSeasonal3InternalPreview = 'Seasonal-2-Release-with-Seasonal-3-Internal-Preview',
  Seasonal3InternalPreviewWithNoReleaseModel = 'Seasonal-3-Internal-Preview-with-No-Release-Model',
  Seasonal3CustomerPreviewWithNoReleaseModel = 'Seasonal-3-Customer-Preview-with-No-Release-Model',
  SilentlyShowNothing = 'Silently-Show-Nothing', // possible if we would otherwise be in Seasonal3InternalPreviewWithNoReleaseModel, but user is not internal.
}

export async function batchFetchObservationTimeSeries(
  queries: ObservationTimeSeriesQuery[],
  requestContext?: RequestContext
): Promise<ApiObservationTimeSeries[]> {
  let responseData: ApiObservationTimeSeries[] = [];
  for (const queryChunk of chunk(queries, 10)) {
    const payload = {
      queries: queryChunk,
    };
    const params = requestContext?.contextOrgId ? {cOrgId: requestContext.contextOrgId} : null;
    const response = await makeApiRequest<ApiObservationTimeSeries[]>(
      'api/v2/timeseries/observations',
      params,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      requestContext?.contextOrgId ? {noContextOrg: true} : {}
    );
    responseData = [...responseData, ...response.data];
  }

  return responseData.map(({dataTimeRange, timestamps, query, data, ...rest}) => {
    return {
      ...rest,
      query: {
        ...query,
        source: query.source as Source,
        startDate: new Date(query.startDate as unknown as string),
        endDate: new Date(query.endDate as unknown as string),
      },
      data: data as unknown as TimeSeriesData,
      dataTimeRange: makeDates(dataTimeRange) as [Date, Date],
      timestamps: makeDates(timestamps),
    };
  });
}

export type ForecastTimeSeriesFetchOptions = {
  includeFlowContributingAreas?: boolean;
  isStaging?: boolean;
  contextOrgId?: string;
};

export async function batchFetchForecastTimeSeries(
  queries: ForecastTimeSeriesQuery[],
  options: ForecastTimeSeriesFetchOptions = {}
): Promise<ApiForecastTimeSeries[]> {
  const {includeFlowContributingAreas, isStaging = false, contextOrgId} = options;
  let responseData: ApiForecastTimeSeries[] = [];
  for (const queryChunk of chunk(queries, 10)) {
    const payload = {
      queries: queryChunk,
      ...(includeFlowContributingAreas ? {includeFlowContributingAreas} : {}),
    };
    const params: Record<string, string | boolean> = isStaging ? {st3Staging: true} : {};
    const effectiveParams = contextOrgId
      ? {...params, cOrgId: contextOrgId}
      : Object.keys(params).length
        ? params
        : null;
    const response = await makeApiRequest<ApiForecastTimeSeries[]>(
      'api/v2/timeseries/forecasts',
      effectiveParams,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      contextOrgId ? {noContextOrg: true} : {}
    );
    responseData = [...responseData, ...response.data];
  }

  return responseData.map(({forecasts, query, ...rest}) => {
    const ts = {
      ...rest,
      query: {
        ...query,
        source: query.source as Source,
      },
      forecasts: forecasts.map((f) => ({
        ...f,
        initializationTime: new Date(f.initializationTime),
        issueTime: new Date(f.issueTime),
        validTimeRange: makeDates(f.validTimeRange) as [Date, Date],
        validTimes: makeDates(f.validTimes),
      })),
    };
    if (query.initializationTimes) {
      ts.query.initializationTimes = makeDates(query.initializationTimes!);
    }
    if (query.initializationTimeEndDate && query.initializationTimeStartDate) {
      ts.query.initializationTimeStartDate = new Date(query.initializationTimeStartDate);
      ts.query.initializationTimeEndDate = new Date(query.initializationTimeEndDate);
    }
    return ts;
  });
}

export async function fetchSeasonalVolumeForecasts(
  query: SeasonalVolumeForecastQuery,
  isStaging: boolean = false
) {
  const response = await makeApiRequest<ApiSeasonalVolumeResponse>(
    'api/v2/seasonalVolumeForecasts',
    isStaging ? {st3Staging: true} : null,
    {method: 'POST', body: JSON.stringify(query)}
  );
  const q = response.data.query;
  response.data.query = {
    ...q,
    initializationTimes: makeDates(q.initializationTimes),
    volumePeriodStartDate: new Date(q.volumePeriodStartDate),
    volumePeriodEndDate: new Date(q.volumePeriodEndDate),
  };

  if (response.data.forecasts.every((f) => !!f.error)) {
    throw new Error(response.data.forecasts[0]?.error);
  }
  response.data.forecasts = [...response.data.forecasts]
    .filter((f) => !f.error)
    .map((f) => {
      return {
        ...f,
        forecastTimeRange: makeDates(f.forecastTimeRange) as [Date, Date],
        initializationTime: new Date(f.initializationTime),
        issueTime: new Date(f.issueTime),
        observedTimeRange: makeDates(f.observedTimeRange) as [Date, Date],
      };
    });

  return response.data;
}

export async function fetchForecastTimes(
  siteId: string,
  sources: Source[],
  startDate: Date,
  endDate: Date,
  isStaging: boolean = false
): Promise<ForecastTimesBySource> {
  const queries: ForecastTimeSeriesQuery[] = sources.map((source) => ({
    siteId,
    source,
    initializationTimeStartDate: startDate,
    initializationTimeEndDate: endDate,
    columns: [],
  }));

  const batches = chunk(queries, 10);
  const batchResponses = await Promise.all(
    batches.map((batch) =>
      makeApiRequest<ApiForecastTimes[]>(
        'api/v2/timeseries/forecastTimes',
        isStaging ? {st3Staging: true} : null,
        {method: 'POST', body: JSON.stringify({queries: batch})}
      ).then((r) => r.data || [])
    )
  );

  const allResponses = batchResponses.flat();
  if (!allResponses.length) {
    return {};
  }

  const forecastsBySource: ForecastTimesBySource = allResponses.reduce(
    (acc, ts) => ({
      ...acc,
      [ts.query.source]: ts.initializationTimes
        .map((initTime, index) => {
          const inputInitializationTimes = !(
            ts.inputInitializationTimes && ts.inputInitializationTimes[index]
          )
            ? null
            : Object.keys(ts.inputInitializationTimes![index]).reduce((acc, source) => {
                return {
                  ...acc,
                  [source]: new Date(ts.inputInitializationTimes![index][source as Source]),
                };
              }, {});
          return {
            initializationTime: new Date(initTime),
            issueTime: new Date(ts.issueTimes[index]),
            inputInitializationTimes,
            firstIssueTime: maybeDate(ts.firstIssueTimes?.[index]),
            lastUpdatedTime: maybeDate(ts.lastUpdatedTimes?.[index]),
            query: ts.query,
          };
        })
        .reverse(),
    }),
    {}
  );

  return forecastsBySource;
}

function makeDates(ds: any[]): Date[] {
  return ds.map((d) => new Date(d));
}

function maybeDate(isoString: string | null | undefined): Date | undefined {
  return isoString ? new Date(isoString) : undefined;
}
